/* eslint-disable no-unused-vars */
import {
  InvoiceAggregate,
  InvoiceNumberFormatValidator,
  InvoiceNumberFormatValidatorSchema,
  RepositoryError,
} from "@invoiceddd/domain";
import { Context, Data, Effect, Layer } from "effect";
import type { InvoiceFilters, Pagination } from "../ports/InvoiceRepository";
import { InvoiceRepository } from "../ports/InvoiceRepository";

/**
 * Use case errors
 */
export class ListInvoicesError extends Data.TaggedError("@/ListInvoicesUseCase/ListInvoicesError")<{
  readonly cause?: unknown;
  readonly message: string;
}> {}

export class InvalidPaginationError extends Data.TaggedError(
  "@/ListInvoicesUseCase/InvalidPaginationError"
)<{
  readonly cause?: unknown;
  readonly message: string;
  readonly page?: number;
  readonly limit?: number;
}> {}

export class InvalidFiltersError extends Data.TaggedError(
  "@/ListInvoicesUseCase/InvalidFiltersError"
)<{
  readonly cause?: unknown;
  readonly message: string;
  readonly invalidField?: string;
}> {}

/**
 * Input for listing invoices
 */
export interface ListInvoicesInput {
  readonly filters?: {
    readonly invoiceNumber?: string;
    readonly dateFrom?: Date;
    readonly dateTo?: Date;
    readonly minTotal?: number;
    readonly maxTotal?: number;
    readonly createdBy?: string;
  };
  readonly pagination?: {
    readonly page?: number;
    readonly limit?: number;
  };
}

/**
 * Output from listing invoices
 */
export interface ListInvoicesOutput {
  readonly invoices: readonly InvoiceAggregate[];
  readonly totalCount: number;
  readonly page: number;
  readonly limit: number;
  readonly totalPages: number;
  readonly hasNextPage: boolean;
  readonly hasPrevPage: boolean;
}

/**
 * Use case for querying invoices with filters and pagination.
 * This use case handles filtering by various criteria and provides
 * paginated results for efficient data handling.
 */
export interface ListInvoicesUseCaseSchema {
  /**
   * List invoices with filtering and pagination
   * @param input - Filters and pagination parameters
   * @returns Effect containing paginated invoices or error
   */
  readonly execute: (
    input: ListInvoicesInput
  ) => Effect.Effect<
    ListInvoicesOutput,
    ListInvoicesError | InvalidPaginationError | InvalidFiltersError | RepositoryError
  >;
}

/**
 * Context tag for ListInvoicesUseCase
 */
export class ListInvoicesUseCase extends Context.Tag("@/UseCases/ListInvoicesUseCase")<
  ListInvoicesUseCase,
  ListInvoicesUseCaseSchema
>() {}

/**
 * Implementation of ListInvoicesUseCase
 */
export const ListInvoicesUseCaseLive = Layer.effect(
  ListInvoicesUseCase,
  Effect.gen(function* () {
    const invoiceRepository = yield* InvoiceRepository;
    const invoiceNumberValidator = yield* InvoiceNumberFormatValidator;

    const implementation: ListInvoicesUseCaseSchema = {
      execute: (input: ListInvoicesInput) => {
        return Effect.gen(function* () {
          // Step 1: Validate and normalize pagination parameters
          const pagination = yield* validateAndNormalizePagination(input.pagination);

          // Step 2: Validate and normalize filters
          const filters = yield* validateAndNormalizeFilters(input.filters, invoiceNumberValidator);

          // Step 3: Query invoices from repository
          const paginatedResult = yield* invoiceRepository.list(filters, pagination).pipe(
            Effect.mapError(
              error =>
                new ListInvoicesError({
                  message: `Failed to list invoices: ${error.message}`,
                  cause: error,
                })
            )
          );

          // Step 4: Convert domain entities to aggregates
          const invoiceAggregates = paginatedResult.invoices.map(invoice =>
            InvoiceAggregate.fromValidatedData(invoice)
          );

          // Step 5: Calculate additional pagination info
          const hasNextPage = pagination.page < paginatedResult.totalPages;
          const hasPrevPage = pagination.page > 1;

          yield* Effect.annotateCurrentSpan({
            hasNextPage,
            hasPrevPage,
            resultCount: invoiceAggregates.length,
          });

          return {
            invoices: invoiceAggregates,
            totalCount: paginatedResult.totalCount,
            page: paginatedResult.page,
            limit: paginatedResult.limit,
            totalPages: paginatedResult.totalPages,
            hasNextPage,
            hasPrevPage,
          };
        }).pipe(
          Effect.withSpan("UseCases.ListInvoices.execute", {
            attributes: {
              filters: JSON.stringify(input.filters),
              pagination: JSON.stringify(input.pagination),
            },
          })
        );
      },
    };

    return ListInvoicesUseCase.of(implementation);
  })
);

/**
 * Validate and normalize pagination parameters
 */
const validateAndNormalizePagination = (pagination?: {
  readonly page?: number;
  readonly limit?: number;
}): Effect.Effect<Pagination, InvalidPaginationError> => {
  return Effect.gen(function* () {
    const page = pagination?.page ?? 1;
    const limit = pagination?.limit ?? 20;

    // Validate page number
    if (page < 1) {
      return yield* Effect.fail(
        new InvalidPaginationError({
          message: "Page number must be greater than 0",
          page,
        })
      );
    }

    // Validate limit
    if (limit < 1) {
      return yield* Effect.fail(
        new InvalidPaginationError({
          message: "Limit must be greater than 0",
          limit,
        })
      );
    }

    // Enforce maximum limit for performance
    if (limit > 100) {
      return yield* Effect.fail(
        new InvalidPaginationError({
          message: "Limit cannot exceed 100 items",
          limit,
        })
      );
    }

    return {
      page,
      limit,
    };
  });
};

/**
 * Validate and normalize filters
 */
const validateAndNormalizeFilters = (
  filters?: {
    readonly invoiceNumber?: string;
    readonly dateFrom?: Date;
    readonly dateTo?: Date;
    readonly minTotal?: number;
    readonly maxTotal?: number;
    readonly createdBy?: string;
  },
  validator?: InvoiceNumberFormatValidatorSchema
): Effect.Effect<InvoiceFilters, InvalidFiltersError> => {
  return Effect.gen(function* () {
    const normalizedFilters: Partial<InvoiceFilters> = {};

    if (filters?.invoiceNumber && validator) {
      // Validate invoice number format using the configurable validator
      yield* validator.validate(filters.invoiceNumber).pipe(
        Effect.mapError(
          error =>
            new InvalidFiltersError({
              message: `Invalid invoice number format: ${error.message}`,
              invalidField: "invoiceNumber",
            })
        )
      );
      Object.assign(normalizedFilters, {
        invoiceNumber: filters.invoiceNumber.trim(),
      });
    }

    if (filters?.dateFrom) {
      // Validate date is not in the future
      if (filters.dateFrom > new Date()) {
        return yield* Effect.fail(
          new InvalidFiltersError({
            message: "Date from cannot be in the future",
            invalidField: "dateFrom",
          })
        );
      }
      Object.assign(normalizedFilters, { dateFrom: filters.dateFrom });
    }

    if (filters?.dateTo) {
      // Validate date is not in the future
      if (filters.dateTo > new Date()) {
        return yield* Effect.fail(
          new InvalidFiltersError({
            message: "Date to cannot be in the future",
            invalidField: "dateTo",
          })
        );
      }
      Object.assign(normalizedFilters, { dateTo: filters.dateTo });
    }

    // Validate date range
    if (filters?.dateFrom && filters?.dateTo && filters.dateFrom > filters.dateTo) {
      return yield* Effect.fail(
        new InvalidFiltersError({
          message: "Date from must be before date to",
          invalidField: "dateRange",
        })
      );
    }

    if (filters?.minTotal !== undefined) {
      if (filters.minTotal < 0) {
        return yield* Effect.fail(
          new InvalidFiltersError({
            message: "Minimum total cannot be negative",
            invalidField: "minTotal",
          })
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- theres no other way currently
      (normalizedFilters as any).minTotal = filters.minTotal;
    }

    if (filters?.maxTotal !== undefined) {
      if (filters.maxTotal < 0) {
        return yield* Effect.fail(
          new InvalidFiltersError({
            message: "Maximum total cannot be negative",
            invalidField: "maxTotal",
          })
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- theres no other way currently
      (normalizedFilters as any).maxTotal = filters.maxTotal;
    }

    // Validate total range
    if (
      filters?.minTotal !== undefined &&
      filters?.maxTotal !== undefined &&
      filters.minTotal > filters.maxTotal
    ) {
      return yield* Effect.fail(
        new InvalidFiltersError({
          message: "Minimum total must be less than or equal to maximum total",
          invalidField: "totalRange",
        })
      );
    }

    if (filters?.createdBy) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- theres no other way currently
      (normalizedFilters as any).createdBy = filters.createdBy.trim();
    }

    return normalizedFilters as InvoiceFilters;
  });
};
